import PropTypes from 'prop-types';
import { BsBuilding } from 'react-icons/bs';

import { validateUrl } from '#lib/urlValidation';

/**
 * Strips a leading http:// or https:// scheme (and any leading whitespace) from the input.
 * Used so the fixed "https://" prefix in the field never doubles up when a user
 * pastes a full URL like "https://www.xyz.de".
 * @param {string} value - Raw input value
 * @returns {string} Value without a leading scheme
 */
const stripScheme = (value) => value.replace(/^\s*https?:\/\//i, '');

const CompanyInputStep = ({ values, onChange, onSkip }) => {
	const handleChange = (e) => {
		onChange({ customerWebsite: stripScheme(e.target.value) });
	};

	return (
		<div className="space-y-5">
			<div className="flex items-start gap-3">
				<div className="h-10 w-10 rounded-control bg-primary-100 text-primary-700 flex items-center justify-center flex-shrink-0">
					<BsBuilding className="w-5 h-5" />
				</div>
				<div>
					<h2 className="text-lg font-semibold text-text">Ihr Unternehmen</h2>
					<p className="text-sm text-bgBase-800">
						Wir analysieren Ihre Website und erstellen daraus eine maßgeschneiderte Vertriebsstrategie.
					</p>
				</div>
			</div>

			<div>
				<label htmlFor="customerWebsite" className="block text-sm font-medium text-text mb-1">
					Website <span className="text-warning-500">*</span>
				</label>
				<div className="flex w-full rounded-card border border-bgBase-500 bg-white shadow-field transition-[border-color,box-shadow] focus-within:border-primary-300 focus-within:shadow-field-focus overflow-hidden">
					<span className="flex items-center px-3 py-2 text-sm text-bgBase-800 bg-bgBase-300 border-r border-bgBase-500 select-none">
						https://
					</span>
					<input
						id="customerWebsite"
						type="text"
						inputMode="url"
						autoCapitalize="none"
						autoCorrect="off"
						spellCheck={false}
						placeholder="www.beispiel.de"
						value={values.customerWebsite ?? ''}
						onChange={handleChange}
						className="flex-1 min-w-0 border-0 bg-transparent rounded-none px-3 py-2 text-sm text-text placeholder:text-bgBase-700 focus:outline-none focus:ring-0"
					/>
				</div>
				<p className="text-xs text-bgBase-800 mt-1">
					Geben Sie einfach Ihre Domain ein – „https://“ ist bereits vorausgefüllt. Wir scrapen Ihre
					Startseite und bis zu 5 Unterseiten für die Analyse.
				</p>
			</div>

			{onSkip && (
				<p className="text-sm text-bgBase-800">
					Keine Website?{' '}
					<button
						type="button"
						onClick={onSkip}
						className="text-primary-600 hover:text-primary-700 font-medium underline underline-offset-2"
					>
						Firmenprofil selbst ausfüllen — dauert 30 Sekunden
					</button>
				</p>
			)}
		</div>
	);
};

CompanyInputStep.propTypes = {
	values: PropTypes.object.isRequired,
	onChange: PropTypes.func.isRequired,
	onSkip: PropTypes.func,
};

CompanyInputStep.isValid = (v) => validateUrl(v.customerWebsite).valid && Boolean(v.customerWebsite?.trim());

/**
 * Returns the normalized, fully-qualified URL for submission (e.g. "https://www.xyz.de"),
 * or null if the input is not a valid URL.
 * @param {Object} v - Step values
 * @param {string} [v.customerWebsite] - User-entered website (without scheme)
 * @returns {string|null}
 */
CompanyInputStep.getNormalizedWebsite = (v) => validateUrl(v.customerWebsite).normalized;

export default CompanyInputStep;
